import React, { useState, useRef } from 'react';
import {
  X,
  Upload,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { uploadFilieresWithSummary } from '@/services/filieresService';
import type { FiliereUploadSummary } from '@/types/filiere.types';

interface UploadFilieresModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'upload' | 'uploading' | 'results';

const UploadFilieresModal: React.FC<UploadFilieresModalProps> = ({ onClose, onSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<FiliereUploadSummary | null>(null);
  const [showCreated, setShowCreated] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.match(/\.(xlsx|xls)$/i)) {
        setError('Veuillez sélectionner un fichier Excel (.xlsx ou .xls)');
        return;
      }
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setStep('uploading');
    setError(null);

    try {
      const result = await uploadFilieresWithSummary(file);
      setSummary(result);
      setStep('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'upload');
      setStep('upload');
    }
  };

  const handleDone = () => {
    onSuccess();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Upload Nomenclature E-DEP</h2>
                <p className="text-sm text-gray-500">Importer les filières depuis un fichier Excel</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step: Upload */}
          {step === 'upload' && (
            <>
              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  file ? 'border-primary-300 bg-primary-50' : 'border-gray-300 hover:border-primary-400'
                }`}
              >
                {file ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="w-12 h-12 text-primary-600 mx-auto" />
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                    <button
                      onClick={() => setFile(null)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Supprimer
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-gray-700">Glissez-déposez votre fichier ici</p>
                      <p className="text-sm text-gray-500">ou</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Parcourir
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>

              {/* Expected Format */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Format attendu :</h4>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>• Colonne A : Établissement (ignoré)</li>
                  <li>• Colonne B : Diplôme</li>
                  <li>• Colonne C : Code Filière</li>
                  <li>• Colonne D : Filière (Intitulé)</li>
                  <li>• Colonne E : Nombre d'années</li>
                  <li>• Colonne F : Année d'accréditation</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 mt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" />
                  Importer
                </button>
              </div>
            </>
          )}

          {/* Step: Uploading */}
          {step === 'uploading' && (
            <div className="py-12 text-center">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-700 font-medium">Import en cours...</p>
              <p className="text-sm text-gray-500 mt-1">Analyse et traitement du fichier</p>
            </div>
          )}

          {/* Step: Results */}
          {step === 'results' && summary && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Créées</span>
                  </div>
                  <div className="text-3xl font-bold text-green-700">{summary.created}</div>
                  <p className="text-sm text-green-600 mt-1">filière(s) ajoutée(s)</p>
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-amber-600" />
                    <span className="font-medium text-amber-800">Ignorées</span>
                  </div>
                  <div className="text-3xl font-bold text-amber-700">{summary.skipped}</div>
                  <p className="text-sm text-amber-600 mt-1">doublon(s)</p>
                </div>
              </div>

              {/* Created List */}
              {summary.createdFilieres.length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowCreated(!showCreated)}
                    className="flex items-center justify-between w-full p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <span className="font-medium text-green-800">
                      Filières créées ({summary.createdFilieres.length})
                    </span>
                    {showCreated ? (
                      <ChevronUp className="w-5 h-5 text-green-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-green-600" />
                    )}
                  </button>
                  {showCreated && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-green-200 rounded-lg">
                      {summary.createdFilieres.map((f, i) => (
                        <div
                          key={i}
                          className="px-3 py-2 border-b border-green-100 last:border-b-0 text-sm"
                        >
                          <span className="font-medium text-gray-900">{f.filiere}</span>
                          <span className="text-gray-500 ml-2">({f.codeFiliere})</span>
                          <span className="text-gray-400 ml-2">• {f.diplome}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Skipped List */}
              {summary.skippedFilieres.length > 0 && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowSkipped(!showSkipped)}
                    className="flex items-center justify-between w-full p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <span className="font-medium text-amber-800">
                      Filières ignorées ({summary.skippedFilieres.length})
                    </span>
                    {showSkipped ? (
                      <ChevronUp className="w-5 h-5 text-amber-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-amber-600" />
                    )}
                  </button>
                  {showSkipped && (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-amber-200 rounded-lg">
                      {summary.skippedFilieres.map((f, i) => (
                        <div
                          key={i}
                          className="px-3 py-2 border-b border-amber-100 last:border-b-0 text-sm"
                        >
                          <div>
                            <span className="font-medium text-gray-900">{f.filiere}</span>
                            <span className="text-gray-500 ml-2">({f.codeFiliere})</span>
                          </div>
                          <div className="text-xs text-amber-600 mt-1">{f.reason}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Warnings */}
              {summary.warnings && summary.warnings.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-yellow-800">Avertissements</span>
                  </div>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    {summary.warnings.slice(0, 5).map((w, i) => (
                      <li key={i}>• {w}</li>
                    ))}
                    {summary.warnings.length > 5 && (
                      <li>... et {summary.warnings.length - 5} autres</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Done Button */}
              <div className="flex items-center justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleDone}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Terminé
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadFilieresModal;
